(function () {
    'use strict';

    angular
        .module('app')
        .factory('EmailTemplate', EmailTemplateFactory);

    EmailTemplateFactory.$inject = ['$q', '$window', 'EmailTemplatesService', 'DialogService'];

    function EmailTemplateFactory($q, $window, EmailTemplatesService, DialogService) {

        /**
         * Get an existing email template.
         *
         * @param {number} emailTemplateId
         * @returns {Promise} A promise that resolves a {EmailTemplate} object for the specified emailTemplateId.
         */
        function get(emailTemplateId) {
            var emailTemplatePromise = EmailTemplatesService.get(emailTemplateId)
                .then(function (data) {
                    var emailTemplate = new EmailTemplate(data);
                    emailTemplate.dirty = false;

                    return emailTemplate;
                });

            return emailTemplatePromise;
        }

        /**
         * Create an email template using an existing email template.
         *
         * @param {number} emailTemplateId
         * @returns {Promise} A promise that resolves a {EmailTemplate} object copied from the specified emailTemplateId.
         */
        function getCopy(emailTemplateId) {
            var emailTemplatePromise = EmailTemplatesService.get(emailTemplateId)
                .then(function (data) {
                    var emailTemplate = new EmailTemplate(data);

                    delete emailTemplate.data.Id;
                    delete emailTemplate.data.UpdatedDate;
                    delete emailTemplate.data.UpdatedBy;
                    delete emailTemplate.data.CreatedDate;
                    delete emailTemplate.data.CreatedBy;
                    delete emailTemplate.data.Name;

                    return $q.when(emailTemplate);
                });

            return emailTemplatePromise;
        }

        /**
         * Create a email template with the supplied data.
         *
         * @returns {Promise} A promise that resolves a {EmailTemplate} object.
         */
        function createWithData(emailTemplateData) {
            var emailTemplate = new EmailTemplate(emailTemplateData);
            emailTemplate.dirty = false;

            return $q.when(emailTemplate);
        }

        /**
         * Get a new email template.
         *
         * @returns {Promise} A promise that resolves a new {EmailTemplate} object.
         */
        function getNew(entityTemplateId) {
            var emailTemplate = new EmailTemplate();

            if (!_.isNil(entityTemplateId) && !isNaN(entityTemplateId)) {
                emailTemplate.data.EntityTemplateId = entityTemplateId;
            }

            emailTemplate.dirty = false;

            return $q.when(emailTemplate);
        }

        /**
         * Gets a list of email templates.
         *
         * @param {int} pageSize - The page sizing for the email template list if using paging. Optional.
         * @param {int} currentPage - The current page for the email template list if using paging. Optional.
         * @param {string} genericSearch - 'Generic' filter for the email template. This matches against the name or description.
         * @param {int} entityTemplateId - The parent entity template. Optional.
         *
         * @returns {Promise} A promise that resolves an array of {EmailTemplate} objects for the specified parameters.
         */
        function getEmailTemplates(pageSize, currentPage, genericSearch, entityTemplateId) {
            return EmailTemplatesService.query({ pageSize: pageSize, currentPage: currentPage, genericSearch: genericSearch, entityTemplateId: entityTemplateId })
                .then(function (data) {
                    var emailTemplates = [];

                    angular.forEach(data.Items, function (emailTemplateData) {
                        emailTemplates.push(new EmailTemplate(emailTemplateData));
                    });

                    data.Items = emailTemplates;

                    return data;
                });
        }

        /**
         * @constructor
         * 
         * @param {object} data - Email template data used to create the Email Template object.
         *                  This is optional, if no data is supplied, a 'new' email template object will be created.
         *
         * @property {Object} data - Email template data, refer C# class 'EmailTemplateConfiguration'.
         */
        function EmailTemplate(data) {
            var vm = this;

            if (!_.isNil(data)) {
                vm.data = data;
            } else {
                vm.data = {
                    Name: '',
                    Description: '',
                    Default: false,
                    Preheader: null,
                    Subject: null,
                    Body: null,
                    FromAddress: null,
                    To: [],
                    Cc: [],
                    Bcc: [],
                    ReplyTo: [],
                    Active: true
                };
            }

            Object.defineProperty(vm, 'dirty', { get: getDirty, set: setDirty });
            vm.dirty = false;

            vm.delete = deleteEmailTemplate;

            // For dirty checking
            var originalData = {};

            /**
             * @returns {bool} true if the model has changed since it was last specified as not dirty.
             */
            function getDirty() {
                return !angular.equals(originalData, vm.data);
            }

            /**
             * @param {bool} Specify whether the model is dirty.
             */
            function setDirty(newDirty) {
                if (newDirty) {
                    originalData = {};
                } else {
                    originalData = angular.copy(vm.data);
                }
            }

            /**
             * Shows a 'confirm deletion dialog' and then deletes the email template if 'Ok' is pressed.
             *
             * @returns {promise} There are three distinct states for the promise:
             *                  Fulfilled - The email template was successfully deleted
             *                  Rejected with null value - The user 'cancelled' the confirm dialog, no attempt was made to delete the email template
             *                  Rejected with value - An attempt was made to delete the email template however it was not successful
             */
            function deleteEmailTemplate() {
                if (!_.isNil(vm.data.Id) && vm.data.Id !== '') {
                    return DialogService
                        .confirm('Delete the Email Template?', 'Delete Email Template confirm')
                        .then(function () {
                            return EmailTemplatesService.remove(vm.data.Id);
                        }, function () {
                            return $q.reject(null);
                        });
                } else {
                    return $q.reject('Email Template does not have an Id value.');
                }
            }
        }

        EmailTemplate.prototype = {};
        EmailTemplate.prototype.constructor = EmailTemplate;

        EmailTemplate.get = get;
        EmailTemplate.getCopy = getCopy;
        EmailTemplate.getNew = getNew;
        EmailTemplate.createWithData = createWithData;
        EmailTemplate.getEmailTemplates = getEmailTemplates;

        return EmailTemplate;
    }
})();